import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
export interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-[#1a0d2e] ${className}`} data-id="element-338">
      <div className="container mx-auto px-4" data-id="element-339">
        {title && <h2 className="font-bold text-center mb-12 text-white text-[16px] md:text-2xl" data-id="element-340">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-341">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-[#0d0716]/70 p-6 rounded-2xl shadow-lg border border-[#3a2456] flex flex-col h-full" data-id="element-342">
              <div className="flex items-center gap-1 mb-4" data-id="element-343">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-slate-300 fill-slate-300" data-id="element-344" />)}
              </div>

              <p className="text-slate-300 italic mb-6 flex-grow" data-id="element-345">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-[#3a2456]/50" data-id="element-346">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-347">
                  {t.initials}
                </div>
                <div data-id="element-348">
                  <p className="font-bold text-white text-sm" data-id="element-349">{t.name}</p>
                  <p className="text-xs text-slate-400" data-id="element-350">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-slate-300/90 mt-0.5" data-id="element-351">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials: Testimonial[] = [{
  initials: 'MR',
  name: 'María R.',
  location: 'Dueña de vinoteca · Palermo, Buenos Aires',
  text: 'Llevaba años atendiendo clientes sin saber exactamente qué decirles sobre los vinos que vendía. Abrí el Tomo 4 de Cata y en una tarde aprendí más que en meses de buscar en Google. Ahora mis clientes me preguntan y tengo respuesta para todo.',
  color: 'bg-purple-700'
}, {
  initials: 'JC',
  name: 'Julián C.',
  location: 'Emprendedor vinoteca · Mendoza',
  text: 'El Tomo 6 de Negocio de Vinoteca me dio el sistema que necesitaba para organizar el stock y dejar de perder plata con vinos que no rotaban. Lo implementé en una semana y los cambios fueron inmediatos.',
  color: 'bg-violet-800'
}, {
  initials: 'SP',
  name: 'Sofía P.',
  location: 'Sommelier y emprendedora · Córdoba',
  text: 'Las 100 Fichas de Degustación del Bono 1 las imprimí y las uso en cada cata que organizo. Mis clientes se van con algo en la mano y eso genera confianza. Nunca pensé que algo tan simple iba a cambiar tanto la experiencia de compra.',
  color: 'bg-fuchsia-900'
}];
export const moreTestimonials: Testimonial[] = [{
  initials: 'PM',
  name: 'Pablo M.',
  location: '🏙️ Buenos Aires',
  text: 'Dos años atendiendo la vinoteca sin saber realmente qué decirle a los clientes sobre los vinos. Con el Megapack en una tarde tenía el argumento de venta para cada botella de la góndola.',
  color: 'bg-purple-900',
  pack: 'Megapack Vinoteca'
}, {
  initials: 'RL',
  name: 'Rosa L.',
  location: '🏙️ Santa Fe',
  text: 'El Tomo 5 de Maridajes Argentinos me dio el script perfecto para la época de asados. Mis ventas de mayo a agosto subieron porque empecé a sugerir el vino para el menú del fin de semana.',
  color: 'bg-violet-700',
  pack: 'Megapack Vinoteca'
}, {
  initials: 'GF',
  name: 'Gabriela F.',
  location: '🏙️ Tucumán',
  text: 'Con las Plantillas para Redes empecé a publicar todos los días sin quedarme sin ideas. En un mes dupliqué los seguidores y tres clientes me dijeron que me habían conocido por Instagram.',
  color: 'bg-purple-800',
  pack: 'Megapack + Nivel Maestro'
}, {
  initials: 'AM',
  name: 'Andrés M.',
  location: '🏙️ Rosario',
  text: 'Cerré la planificación de mi vinoteca para la temporada de fiestas en dos horas usando el Tomo 6. Antes me llevaba semanas y siempre quedaba algo sin resolver.',
  color: 'bg-fuchsia-800',
  pack: 'Megapack + Nivel Maestro'
}, {
  initials: 'SV',
  name: 'Silvia V.',
  location: '🏙️ Córdoba',
  text: 'El Catálogo de Varietales lo imprimí y se lo doy a los clientes nuevos. Vuelven a comprar porque se llevan algo de valor que los ayuda a elegir mejor la próxima vez.',
  color: 'bg-violet-900',
  pack: 'Megapack Vinoteca'
}];